import { useState } from 'react';
import { Alert, Button, DatePicker, Modal, Skeleton, Table, Tag, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getEinvoiceReminderPreview } from '@/services/einvoiceReminders.service';
import EinvoiceMailPreview from './EinvoiceMailPreview';

const fmt = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '—');
const money = (v) => Number(v || 0).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Dry run of the monthly e-invoice reminder: every company with open incoming
 * invoices from before the send day's month, who would receive the mail and
 * why it would not go out. Nothing is sent from here.
 */
export default function EinvoiceReminderPreviewModal({ open, onClose }) {
  const { t } = useTranslation('einvoiceReminders');
  const [date, setDate] = useState(null);
  const [mailOf, setMailOf] = useState(null);
  const day = date ? date.format('YYYY-MM-DD') : undefined;
  const { data, isLoading, error } = useQuery({
    queryKey: ['einvoice-reminder-preview', day || ''],
    queryFn: () => getEinvoiceReminderPreview(day),
    enabled: open,
  });

  const columns = [
    {
      title: t('preview.company'),
      dataIndex: 'name',
      width: 240,
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v} placement="topLeft">
          <span>{v}</span>
        </Tooltip>
      ),
    },
    { title: t('preview.count'), dataIndex: 'count', width: 80, align: 'right' },
    { title: t('preview.oldest'), dataIndex: 'oldestDate', width: 100, render: fmt },
    {
      title: t('preview.to'),
      dataIndex: 'to',
      width: 220,
      ellipsis: { showTitle: false },
      render: (v) =>
        v.length ? (
          <Tooltip title={v.join(', ')} placement="topLeft">
            <span>{v.join(', ')}</span>
          </Tooltip>
        ) : (
          <span className="text-red-600">{t('preview.noTo')}</span>
        ),
    },
    {
      title: t('preview.cc'),
      dataIndex: 'cc',
      width: 220,
      ellipsis: { showTitle: false },
      render: (v) => (
        <Tooltip title={v.join(', ')} placement="topLeft">
          <span className="opacity-80">{v.join(', ') || '—'}</span>
        </Tooltip>
      ),
    },
    { title: t('preview.language'), dataIndex: 'language', width: 80, render: (v) => t(`language.${v}`) },
    {
      title: t('preview.state'),
      key: 'state',
      width: 170,
      render: (_, r) =>
        r.wouldSend ? (
          <Tag color="green" className="m-0">{t('preview.wouldSend')}</Tag>
        ) : (
          <Tag color={r.reason === 'NO_RECIPIENTS' ? 'red' : 'default'} className="m-0">
            {t(`reason.${r.reason}`)}
          </Tag>
        ),
    },
    {
      title: '',
      key: 'mail',
      width: 56,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={t('preview.showMail')}>
          <Button shape="circle" size="small" icon={<EyeOutlined />} onClick={() => setMailOf(r)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={1240} title={t('preview.title')} destroyOnClose>
      <div className="grid gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span>{t('preview.from')}</span>
          <DatePicker value={date} onChange={setDate} format="DD.MM.YYYY" allowClear placeholder={t('preview.today')} />
          {data && (
            <span>
              {t('preview.sendDay', {
                date: fmt(data.sendDay.date),
                round: data.sendDay.round,
                total: data.totalRounds,
                from: fmt(data.periodStart),
                cutoff: fmt(dayjs(data.cutoff).subtract(1, 'day')),
              })}
            </span>
          )}
        </div>
        {data && !data.globalEnabled && <Alert type="info" showIcon className="!static" message={t('preview.globalOff')} />}
        {data && (
          <div className="text-sm">
            {t('preview.totals', {
              companies: data.totals.companies,
              invoices: data.totals.invoices,
              wouldSend: data.totals.wouldSend,
              ready: data.totals.readyOnceOn,
            })}
          </div>
        )}
        {isLoading && <Skeleton active />}
        {error && <Alert type="error" showIcon className="!static" message={error?.response?.data?.message || error.message} />}
        {data && (
          <Table
            size="small"
            rowKey="companyId"
            columns={columns}
            dataSource={data.items}
            tableLayout="fixed"
            scroll={{ x: 1166 }}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            expandable={{
              expandedRowRender: (r) => (
                <Table
                  size="small"
                  rowKey="_id"
                  pagination={false}
                  dataSource={r.invoices}
                  tableLayout="fixed"
                  columns={[
                    { title: t('invoice.supplier'), dataIndex: 'supplierName', ellipsis: true },
                    { title: t('invoice.number'), dataIndex: 'number', width: 170 },
                    { title: t('invoice.date'), dataIndex: 'date', width: 100, render: fmt },
                    { title: t('invoice.amount'), key: 'amount', width: 150, align: 'right', render: (_, i) => `${money(i.amount)} ${i.currency}` },
                    { title: t('invoice.status'), dataIndex: 'status', width: 130, render: (s) => t(`invoiceStatus.${s}`) },
                  ]}
                  footer={r.count > r.invoices.length ? () => t('preview.more', { count: r.count - r.invoices.length }) : undefined}
                />
              ),
            }}
          />
        )}
      </div>
      <EinvoiceMailPreview companyId={mailOf?.companyId} companyName={mailOf?.name} date={day} onClose={() => setMailOf(null)} />
    </Modal>
  );
}
